import { useFormNotification, useTranslate } from "@commonlibs";
import { config } from "common/config";
import { formatTokenAmount, parseTokenAmount } from "features/wallet/utils";
import { NumericTextFieldProps } from "ui/common/components/input/NumericTextField/NumericTextField.types";
import { PointsBalanceFieldProps } from "../PointsBalanceField";

export interface UsePointsBalanceValidation extends Pick<PointsBalanceFieldProps, "validators" | "error" | "name"> {
    value: string;
}

export interface UsePointsBalanceValidationResult extends Pick<NumericTextFieldProps, "error"> {}

export default function usePointsBalanceValidation({
    validators: { gt, gte, lte } = {},
    error,
    value = "0",
    name,
}: UsePointsBalanceValidation): UsePointsBalanceValidationResult {
    const errorIsBoolean = typeof error === "boolean";
    const translate = useTranslate();
    const decimals = config.organization.decimals || 0;

    const bigIntValue = value !== "" ? parseTokenAmount(value, decimals) : "0";

    const isGtError = !!bigIntValue && !!gt && BigInt(bigIntValue) <= BigInt(gt); // skipcq: JS-0066
    const formattedGtBalance = formatTokenAmount(gt || "0", decimals);
    const gtError: [boolean, string] = [isGtError, translate("invalid_number_gt", { ns: "error", n: formattedGtBalance })];

    const isGteError = !!bigIntValue && !!gte && BigInt(bigIntValue) < BigInt(gte); // skipcq: JS-0066
    const formattedGteBalance = formatTokenAmount(gte || "0", decimals);
    const gteError: [boolean, string] = [isGteError, translate("invalid_number_gte", { ns: "error", n: formattedGteBalance })];

    const isLteError = !!bigIntValue && !!lte && BigInt(bigIntValue) > BigInt(lte); // skipcq: JS-0066
    const lteError: [boolean, string] = [isLteError, translate("invalid_number_lte", { ns: "error" })];

    const isErrorProp = (Array.isArray(error) && error[0]) || (errorIsBoolean && error);
    const isError = isGtError || isGteError || isLteError || isErrorProp;

    useFormNotification(name, bigIntValue, !isError);

    const errorObj = {
        ...(isGtError && { error: gtError }),
        ...(isGteError && { error: gteError }),
        ...(isLteError && { error: lteError }),
        ...(isErrorProp && { error }),
    };

    return errorObj;
}
